import { useState, useEffect, useCallback } from "react";
import {
  getTradingConfig,
  updateTradingConfig,
  type TradingConfig,
} from "../../services/adminService";

interface ConfigToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  color?: string;
}

function ConfigToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
  color = "blue",
}: ConfigToggleProps) {
  const colorClasses = {
    blue: "bg-blue-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500",
    red: "bg-red-500",
  };

  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
      <div className="flex-1">
        <div className="text-sm font-medium text-surface-100">{label}</div>
        <div className="text-xs text-surface-400 mt-0.5">{description}</div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-surface-900 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? colorClasses[color as keyof typeof colorClasses] : "bg-surface-600"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

export function TradingConfigPanel() {
  const [config, setConfig] = useState<TradingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTradingConfig();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trading config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) {
      loadConfig();
    }
  }, [expanded, loadConfig]);

  const handleToggle = async (field: keyof TradingConfig, value: boolean) => {
    if (!config || saving) return;

    try {
      setSaving(true);
      setError(null);

      const update = { [field]: value };
      const updated = await updateTradingConfig(update);
      setConfig(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update config");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-surface-100">Trading Config</div>
            <div className="text-xs text-surface-400">
              Configure trading behavior and dev mode settings
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-surface-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-surface-700/50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-400">{error}</p>
              <button
                onClick={loadConfig}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300"
              >
                Try again
              </button>
            </div>
          ) : config ? (
            <div className="space-y-3">
              {/* Warning banner for dev settings */}
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400">
                  ⚠️ These settings affect live trading behavior. Use with caution in production.
                </p>
              </div>

              <ConfigToggle
                label="Use GTC Orders"
                description="Use Good-Til-Canceled instead of Day orders. Orders persist until filled."
                checked={config.use_gtc_orders}
                onChange={(v) => handleToggle("use_gtc_orders", v)}
                disabled={saving}
                color="blue"
              />

              <ConfigToggle
                label="Bypass Market Hours"
                description="Allow placing orders when market is closed. Orders queue for next open."
                checked={config.bypass_market_hours}
                onChange={(v) => handleToggle("bypass_market_hours", v)}
                disabled={saving}
                color="amber"
              />

              <ConfigToggle
                label="Skip Order Fill Wait"
                description="Don't wait for order fills. Treat 'accepted' as success (dev mode)."
                checked={config.skip_order_fill_wait}
                onChange={(v) => handleToggle("skip_order_fill_wait", v)}
                disabled={saving}
                color="purple"
              />

              <ConfigToggle
                label="Dry Run Mode"
                description="Log trades without submitting to broker. For testing only."
                checked={config.dry_run_mode}
                onChange={(v) => handleToggle("dry_run_mode", v)}
                disabled={saving}
                color="red"
              />

              {/* Last updated info */}
              {config.updated_at && (
                <div className="pt-2 text-xs text-surface-500 text-right">
                  Last updated: {new Date(config.updated_at).toLocaleString()}
                  {config.updated_by && ` by ${config.updated_by}`}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
